<script lang="ts">
  /* A date field that is written day first — dd.mm.yyyy — whatever the
     browser's language (owner, 2026-09-29: «везде формат день / месяц / год»).

     A native <input type="date"> shows the date in the browser's own format:
     an English Chrome writes 10/01/2026 for the 1st of October, and the page
     cannot change that. So the field is text, read day first («29.09.2026»,
     «29/9/26», «29092026»), and the calendar button opens the browser's own
     picker from a hidden native field. The value stays «YYYY-MM-DD» — what
     every form and the API already speak — so `bind:value` works as before.

     `onchange(value)` runs when a new date is committed: on leaving the field,
     on Enter, or on a pick. Something that is no date puts the last good one
     back. */
  import { _ } from "svelte-i18n";
  import { dayToIso, isoToDay } from "$lib/format";

  let {
    value = $bindable(""),
    onchange,
    onkeydown,
    id,
    class: className = "",
    disabled = false,
    compact = false,
    min,
    max,
    "aria-label": ariaLabel,
  }: {
    value?: string | null;
    onchange?: (value: string) => void;
    onkeydown?: (e: KeyboardEvent) => void;
    id?: string;
    class?: string;
    disabled?: boolean;
    /** Table cells: a smaller field. */
    compact?: boolean;
    min?: string;
    max?: string;
    "aria-label"?: string;
  } = $props();

  let text = $state(isoToDay(value));
  let typing = false;
  let native: HTMLInputElement | undefined = $state();

  /* A value set from outside shows at once — unless the field is being typed in. */
  $effect(() => {
    const v = value;
    if (!typing) text = isoToDay(v);
  });

  function set(iso: string) {
    text = isoToDay(iso);
    if (iso === (value ?? "")) return;
    value = iso;
    onchange?.(iso);
  }

  function commit() {
    typing = false;
    const iso = dayToIso(text);
    if (iso === null) {
      text = isoToDay(value);
      return;
    }
    set(iso);
  }

  function openPicker() {
    if (!native || disabled) return;
    try {
      native.showPicker();
    } catch {
      native.focus();
      native.click();
    }
  }
</script>

<span class="date-input {className}" class:compact class:disabled>
  <input
    {id}
    type="text"
    class="di-text"
    inputmode="numeric"
    autocomplete="off"
    placeholder={$_("common.datePlaceholder")}
    maxlength="10"
    value={text}
    {disabled}
    aria-label={ariaLabel}
    oninput={(e) => {
      typing = true;
      text = e.currentTarget.value;
    }}
    onblur={commit}
    onkeydown={(e) => {
      if (e.key === "Enter") commit();
      onkeydown?.(e);
    }}
  />
  <button
    type="button"
    class="di-pick"
    tabindex="-1"
    {disabled}
    title={$_("common.pickDate")}
    aria-label={$_("common.pickDate")}
    onclick={openPicker}
  >
    <span class="material-icons-outlined" aria-hidden="true">calendar_today</span>
  </button>
  <input
    bind:this={native}
    type="date"
    class="di-native"
    tabindex="-1"
    aria-hidden="true"
    value={value ?? ""}
    {min}
    {max}
    {disabled}
    onchange={(e) => set(e.currentTarget.value)}
  />
</span>

<style>
  .date-input { position: relative; display: inline-flex; align-items: center; width: 100%; min-width: 0; }
  /* Two classes deep: a surrounding .field's input rule must not take the
     room the calendar button sits in. */
  .date-input .di-text {
    width: 100%; min-width: 0; box-sizing: border-box;
    padding: 6px 30px 6px 8px; font: inherit; font-size: 13px; font-variant-numeric: tabular-nums;
    background: #fff; color: var(--color-text); border: 1px solid var(--color-border, #d0d4d8); border-radius: 6px;
  }
  .date-input .di-text:focus { outline: none; border-color: var(--color-primary); }
  .date-input .di-text:disabled { background: #f5f6f7; color: var(--color-text-muted); }
  .date-input.compact .di-text { padding: 4px 26px 4px 6px; border-radius: 4px; border-color: var(--color-divider); }
  .di-pick {
    position: absolute; right: 3px; top: 50%; transform: translateY(-50%);
    display: inline-flex; align-items: center; justify-content: center;
    width: 24px; height: 24px; padding: 0; border: 0; border-radius: 4px; background: none;
    color: var(--color-text-muted); cursor: pointer;
  }
  .di-pick:hover:not(:disabled) { color: var(--color-primary); background: var(--color-primary-soft); }
  .di-pick:disabled { cursor: default; opacity: .5; }
  .di-pick .material-icons-outlined { font-size: 17px; }
  .compact .di-pick { width: 22px; height: 22px; }
  .compact .di-pick .material-icons-outlined { font-size: 15px; }
  /* The browser's picker opens from here: present, but not seen. */
  .di-native {
    position: absolute; right: 0; bottom: 0; width: 1px; height: 1px;
    opacity: 0; pointer-events: none; border: 0; padding: 0;
  }
</style>
